import React from "react";
import { SqiAlertIcon, TriangleAlertIcon, FreeFallIcon, ClinicalMetricIcon, ClinicalMetricIconPriority, ExcessiveMotionIcon, CalibrationOverdueIcon, MemoryFullIcon, DeviceRemovedIcon, DeviceSelfTestFailIcon } from "../components/icons/Icons";
import { convertMeasurementValue } from "./unitUtils";
import { formatDateWithFullMonth, formatTime, formatDateWithFullMonthAndTime } from "./dateUtils";

/**
 * Display name for an alert (business name preferred).
 * @param {Object} alert
 * @returns {string}
 */
export function getAlertDisplayName(alert) {
  return alert.alert_display_name ?? alert.alert_name;
}

/**
 * Formats created/updated timestamps for alarm detail UI.
 * @param {Object} item - Alert row
 * @param {string} dateFormat - User date format preference
 * @returns {JSX.Element}
 */
export function formatCompletedDate(item, dateFormat) {
  const formattedTimeCreated = formatTime(item.createdDate);
  const formattedDateCreated = formatDateWithFullMonth(item.createdDate, dateFormat);
  const formattedTimeUpdate = formatTime(item.alert_lastUpdatedTimestamp);
  const formattedDateUpdate = formatDateWithFullMonth(item.alert_lastUpdatedTimestamp, dateFormat);
  if (formattedTimeCreated === formattedTimeUpdate) {
    return (
      <div style={{ display: "flex", columnGap: "10px", alignItems: "center" }}>
        <span className="body-2" style={{ borderRight: "1px solid #9D9D9E", borderLeft: "1px solid #9D9D9E", padding: "0 10px", fontWeight: "500" }}>{formattedTimeCreated}</span>
        <span className="body-2" style={{ fontWeight: "500" }}>{formattedDateCreated}</span>
      </div>
    );
  }
  if (!formattedTimeUpdate) {
    return (
      <div style={{ display: "flex", columnGap: "10px", alignItems: "center" }}>
        <span className="body-2" style={{ borderRight: "1px solid #9D9D9E", borderLeft: "1px solid #9D9D9E", padding: "0 10px", fontWeight: "500" }}>Created: {formattedTimeCreated}</span>
        <span className="body-2" style={{ fontWeight: "500" }}>{formattedDateCreated}</span>
      </div>
    );
  }
  if (formattedDateCreated !== formattedDateUpdate) {
    const formattedCreatedDateAndTime = `Created: ${formatDateWithFullMonthAndTime(item.createdDate, dateFormat)}`;
    const formattedUpdatedDateAndTime = `Updated: ${formatDateWithFullMonthAndTime(item.alert_lastUpdatedTimestamp, dateFormat)}`;
    return (
      <div style={{ display: "flex", alignItems: "center" }}>
        <span className="body-2" style={{ borderLeft: "1px solid #9D9D9E", padding: "0 10px", fontWeight: "500" }}>{formattedCreatedDateAndTime}</span>
        <span className="body-2" style={{ borderLeft: "1px solid #9D9D9E", padding: "0 10px", fontWeight: "500" }}>{formattedUpdatedDateAndTime}</span>
      </div>
    );
  }
  return (
    <div style={{ display: "flex", columnGap: "10px", alignItems: "center" }}>
      <span className="body-2" style={{ borderRight: "1px solid #9D9D9E", borderLeft: "1px solid #9D9D9E", padding: "0 10px", fontWeight: "500" }}>Created: {formattedTimeCreated}, Updated: {formattedTimeUpdate}</span>
      <span className="body-2" style={{ fontWeight: "500" }}>{formattedDateCreated}</span>
    </div>
  );
}

/**
 * Sort function for patient alerts.
 * First sorts by alert priority (high > medium > low), 
 * then by createdDate in descending order (newest first) if priorities are equal.
 *
 * @param {Object} a - First patient alert to compare.
 * @param {Object} b - Second patient alert to compare.
 * @returns {number} - Sorting order value for Array.prototype.sort().
 */
export function alertPrioritySort(a, b) {
  const priorityOrder = {
    low: 1,
    medium: 2,
    high: 3,
  };

  // Safe check for null/undefined alert_priority: treat as "low"
  const getPriority = (alert) => {
    let priority = alert?.alert_priority;
    if (!priority || typeof priority !== "string") priority = "low";
    return priorityOrder[(priority || "low").toLowerCase()] || priorityOrder["low"];
  };

  const priorityDiff = getPriority(b) - getPriority(a);
  if (priorityDiff !== 0) {
    return priorityDiff;
  }

  const timestampA = getAlertTimestamp(a);
  const timestampB = getAlertTimestamp(b);
  const aTime = new Date(timestampA).getTime();
  const bTime = new Date(timestampB).getTime();
  return bTime - aTime;
};

/**
 * Returns the correct timestamp based on alert type
 *
 * @param {Object} alertItem - An alert item from the alerts table
 * @returns {String} - UTC timestamp
 */
export function getAlertTimestamp(alert) {
  return alert.alert_lastUpdatedTimestamp || alert.createdDate;
};


/**
 * Returns an icon color, background color, and border color 
 * based on the alert_color attribute from the alerts table.
 * @param {string} alert_color - The alert color type ("red", "yellow", etc.).
 * @returns {{color: string, background: string, border: string}} An object containing the text color, background color, and border color.
 */
export function getAlertColors(alert_color) {
  let color, background, border;
  switch (alert_color) {
    case "red":
      color = "#9F1919";
      border = '#DB7878';
      background = '#F7E2E2';
      break;
    case "yellow":
      color = "#BA9000";
      border = '#946705';
      background = '#FDF3CF';
      break;
    default:
      color = "#BA9000";
      border = '#946705';
      background = '#FDF3CF';
      break;
  }
  return { color, background, border }
};

/**
 * Returns an icon color based on the alert_color attribute from the alerts table.
 * @param {string} color - The alert color type ("red", "yellow", etc.).
 * @returns {string} - a string containing the color's hexcode
 */
export function getAlertIconColor(color) {
  switch (color) {
    case "red":
      return "#9F1919";
    case "yellow":
      return "#BA9000";
    default:
      return "#BA9000";
  }
};

/**
 * Returns an icon color, background color, and border color 
 * based on the alert_color attribute from the alerts table.
 * @param {string} alert_color - The alert color type ("red", "yellow", etc.).
 * @returns {{color: string, background: string, border: string}} An object containing the text color, background color, and border color.
 */
export function getAlertColorsAndIcon(alert, justIcon = false, justColors = false) {
  let color, background, border;
  switch (alert.alert_color) {
    case "red":
      color = "#9F1919";
      border = '#DB7878';
      background = '#F7E2E2';
      break;
    case "yellow":
      color = "#BA9000";
      border = '#946705';
      background = '#FDF3CF';
      break;
    default:
      color = "#BA9000";
      border = '#946705';
      background = '#FDF3CF';
      break;
  }

  if (justColors) {
    return { color, background, border };
  }

  let icon;
  if (alert.alert_subType === "fall status") {
    icon = <FreeFallIcon color={color} />
  } else if (alert.alert_subType === "calibration overdue") {
    icon = <CalibrationOverdueIcon color={color} />
  } else if (alert.alert_category === "clinical") {
    if (alert.alert_priority === 'low') {
      icon = <ClinicalMetricIcon color={color} />
    } else {
      icon = <ClinicalMetricIconPriority color={color} />
    }
  } else if (alert.alert_description?.toLowerCase().includes("signal quality")) {
    icon = <SqiAlertIcon />
    color = "#212121";
  } else if (alert.alert_name?.toLowerCase().includes("memory full")) {
    // memory full
    icon = <MemoryFullIcon />
  } else if (alert.alert_hover_display?.toLowerCase().includes("excessive motion")) {
    // excessive motion
    icon = <ExcessiveMotionIcon />
  } else if (alert.alert_name?.toLowerCase().includes("self test")) {
    // self test
    icon = <DeviceSelfTestFailIcon />
  } else if (alert.alert_name?.toLowerCase().includes("lead off")) {
    // ecg lead off
    icon = <DeviceRemovedIcon />
  } else {
    // default to triangle
    icon = <TriangleAlertIcon color={color} />
  }
  if (justIcon) {
    return icon;
  } else return { icon, color, background, border };
};


/**
 * Returns a boolean based on an alert to determine if the alert is silenced.
 * Checks if alert has a silence timestamp and if so, checks whether the timestamp is in the future
 * @param {Object} alertItem - An alert item from the alerts table
 * @returns {boolean} - returns a boolean representing whether the alert is currently silenced
 */
export function isItemActivelySilenced(alertItem) {
  if (alertItem.alert_silenceTimestamp) {
    const now = new Date();
    const silenceUntil = new Date(alertItem.alert_silenceTimestamp);
    return silenceUntil > now;
  }
  return false;
};

/**
 * Returns a string for the chart name corresponding to a modality id.
 * @param {number} modalityId - the modality id.
 * @returns {string} - the chart name string
 */
export function getChartName(modalityId) {
  switch (modalityId) {
    case 1:
      return 'temperature'
    case 3:
      return 'oxygenSaturation'
    case 4:
      return 'ECG'
    case 5, 10:
      return 'bloodPressure'
    case 6:
      return 'respiratoryRate'
    case 7:
      return 'AUSC'
    case 8:
      return 'weight'
    case 11:
      return 'glucoseLevel'
    case 13:
      return 'steps'
    case 15:
      return 'ECGHRV'
    default:
      return ''
  }
};

/**
 * Formats the alert description based on the rule type and user preference.
 * For enrollment alerts, if the description starts with a value and a known prefix, formats the value according to user unit preference, then appends any remaining text after the prefix.
 * @param {Object} alertItem - An alert item from the alerts table
 * @returns {string} - formatted description
 */
export function formatAlertDescription(alertItem, userUnitPreference) {
  if (!alertItem.alert_subType && !alertItem.alert_description && !alertItem.alert_description_business) {
    return 'No description';
  }

  const description = alertItem.alert_description_business ?? alertItem.alert_description;
  // Match any number (integer or decimal) at the start, followed by at least one space, then the rest
  const regex = /^(\d+(?:\.\d+)?)(\s+.+)$/;
  const match = description.match(regex);
  if (match) {
    const originalValue = parseFloat(match[1]);
    let convertedValue = convertMeasurementValue(originalValue, alertItem.alert_modalityId, userUnitPreference);
    if (alertItem.alert_modalityId && (alertItem.alert_modalityId === 1 || alertItem.alert_name?.toLowerCase().includes("temperature"))) {
      convertedValue = parseFloat(convertedValue).toFixed(1);
    } else {
      convertedValue = Math.round(convertedValue);
    }
    return `${convertedValue}${match[2]}`;
  }

  return description;
};


export function getAlertSilenceTimestamp(minutesDuration) {
  const todayPlusDuration = new Date();
  todayPlusDuration.setMinutes(todayPlusDuration.getMinutes() + minutesDuration);
  return todayPlusDuration.toISOString();
};

export const KNOWN_TECHNICAL_KEYWORDS = ["memory full", "self test", "lead off", "excessive motion"];

/**
 * True when alert name or hover text matches any {@link KNOWN_TECHNICAL_KEYWORDS} (case-insensitive).
 * @param {Object} alert - Alert row from the alerts table
 * @returns {boolean}
 */
export function isKnownTechnicalAlert(alert) {
  const alertName = alert?.alert_name?.toLowerCase() ?? "";
  const alertHover = alert?.alert_hover_display?.toLowerCase() ?? "";
  return KNOWN_TECHNICAL_KEYWORDS.some(
    (keyword) => alertName.includes(keyword) || alertHover.includes(keyword)
  );
}

/**
 * Technical alerts that are not covered by known keywords and are not generic device status.
 * Used for grouping "other" technical issues (e.g. overview device issue icon).
 * @param {Object[]} alerts - List of technical alerts
 * @returns {Object[]}
 */
export function filterUnknownTechnicalAlerts(alerts) {
  if (!Array.isArray(alerts)) {
    return [];
  }
  return alerts.filter(
    (item) => !isKnownTechnicalAlert(item) && item.alert_subType !== "device status"
  );
}

/**
 * Technical alerts with subtype "device status" that are not keyword-known are excluded from overview counts/filters.
 * Known technical alerts (e.g. memory full, lead off) still count even when subtype is device status.
 * @param {Object} item - Alert row from the alerts table
 * @returns {boolean}
 */
export function alertCountsTowardOverview(item) {
  if (!item) return false;
  if (
    item.alert_category === "technical" &&
    item.alert_subType === "device status" &&
    !isKnownTechnicalAlert(item)
  ) {
    return false;
  }
  return true;
}